import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  getQuestionList,
  createQuestion,
  updateQuestion,
  Question,
  CreateQuestionPayload,
  UpdateQuestionPayload,
} from "../../apis/questionApis";

/* ---------- State ---------- */

interface QuestionState {
  list: Question[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

const initialState: QuestionState = {
  list: [],
  loading: false,
  submitting: false,
  error: null,
};

/* ---------- Thunks ---------- */
/* (UNCHANGED — keeping exactly as you wrote them) */

export const fetchQuestionList = createAsyncThunk(
  "questions/fetchList",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getQuestionList();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch questions");
    }
  },
);

export const createQuestionThunk = createAsyncThunk(
  "questions/create",
  async (payload: CreateQuestionPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await createQuestion(payload);

      if (response.status) {
        dispatch(fetchQuestionList());
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create question");
    }
  },
);

export const updateQuestionThunk = createAsyncThunk(
  "questions/update",
  async (
    { id, payload }: { id: string; payload: UpdateQuestionPayload },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await updateQuestion(id, payload);

      if (response.status) {
        dispatch(fetchQuestionList());
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to update question");
    }
  },
);

export const deleteQuestionThunk = createAsyncThunk(
  "questions/delete",
  async (question: Question, { dispatch, rejectWithValue }) => {
    try {
      const payload: UpdateQuestionPayload = {
        questionName: question.questionName,
        questionDescription: "",
        isActive: false,
        options: question.options.map((opt) => ({
          optionName: opt.optionName,
          optionOrder: opt.optionOrder,
        })),
      };

      const response = await updateQuestion(question.id, payload);

      if (response.status) {
        dispatch(fetchQuestionList());
      }

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to delete question");
    }
  },
);

/* ---------- Slice ---------- */

const questionSlice = createSlice({
  name: "questions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    const getErrorMessage = (payload: unknown, fallback: string) =>
      typeof payload === "string" ? payload : fallback;

    builder
      /* ---- Fetch ---- */
      .addCase(fetchQuestionList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchQuestionList.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
      })
      .addCase(fetchQuestionList.rejected, (state, action) => {
        state.loading = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to fetch questions",
        );
        toast.error(state.error);
      })

      /* ---- Create ---- */
      .addCase(createQuestionThunk.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createQuestionThunk.fulfilled, (state, action) => {
        state.submitting = false;

        const message = "Question created successfully";

        toast.success(message);
      })
      .addCase(createQuestionThunk.rejected, (state, action) => {
        state.submitting = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to create question",
        );
        toast.error(state.error);
      })

      /* ---- Update ---- */
      .addCase(updateQuestionThunk.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(updateQuestionThunk.fulfilled, (state, action) => {
        state.submitting = false;

        const message = action.payload?.data || "Question updated successfully";

        toast.success(message);
      })
      .addCase(updateQuestionThunk.rejected, (state, action) => {
        state.submitting = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to update question",
        );
        toast.error(state.error);
      })

      /* ---- Delete ---- */
      .addCase(deleteQuestionThunk.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(deleteQuestionThunk.fulfilled, (state, action) => {
        state.submitting = false;
        toast.success("Question deleted successfully");
      })
      .addCase(deleteQuestionThunk.rejected, (state, action) => {
        state.submitting = false;
        state.error = getErrorMessage(
          action.payload,
          "Failed to delete question",
        );
        toast.error(state.error);
      })

  },
});

export default questionSlice.reducer;
